'use client'

import { useMemo, useState } from 'react'

import { ColumnFiltersState, OnChangeFn } from '@tanstack/react-table'
import { isError } from 'lodash'
import { SearchIcon } from 'lucide-react'

import { FilterPopoverButton } from '~/app/accounting/filters/FilterPopoverButton'
import { FilterScrollFade } from '~/app/accounting/filters/FilterScrollFade'
import {
  COLUMN_IDS,
  getColumnFilterValue,
  setColumnFilterValue,
} from '~/app/accounting/filters/helpers'
import { useImportedAccounts } from '~/app/accounting/useImportedAccounts'
import { HighlightedText } from '~/components/HighlightedText'
import { SplitsAvatar } from '~/components/SplitsAvatar'
import { parseMultiSelectFilterValue } from '~/components/Table'
import {
  AddressDisplay,
  CheckboxGroupInput,
  EmptyState,
  Group,
} from '~/components/component-library'
import { Input } from '~/components/inputs/Input'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import { IAddress, OrgSmartAccount, SmartAccountRole } from '~/types'

type AccountOption = {
  id: string
  name: string
  address: IAddress
  // Shown beside accounts the default scope leaves out.
  tag?: string
  // Imported accounts aren't in the org's address labels, so AddressDisplay
  // can't resolve their name; render it directly instead.
  showOwnName?: boolean
}

const toAccountOption = (account: OrgSmartAccount): AccountOption => ({
  id: account.id,
  name: account.name,
  address: account.address,
  tag: account.isArchived ? 'Archived' : undefined,
})

export const AccountFilterButton = ({
  columnFilters,
  setColumnFilters,
}: {
  columnFilters: ColumnFiltersState
  setColumnFilters: OnChangeFn<ColumnFiltersState>
}) => {
  const { smartAccounts } = useSmartAccounts({
    showArchived: true,
    includeAutomations: true,
    orderedByValue: true,
  })
  const { importedAccounts } = useImportedAccounts()

  const mainAccounts = smartAccounts
    .filter(
      (account) =>
        !account.isArchived && account.role !== SmartAccountRole.AUTOMATION,
    )
    .map(toAccountOption)
  // Below the fold, and only in scope when picked: automations, then archived
  // accounts, then imported accounts.
  const collapsedAccounts: AccountOption[] = [
    ...smartAccounts
      .filter(
        (account) =>
          !account.isArchived && account.role === SmartAccountRole.AUTOMATION,
      )
      .map(toAccountOption),
    ...smartAccounts
      .filter((account) => account.isArchived)
      .map(toAccountOption),
    ...importedAccounts.map((account) => ({
      id: account.id,
      name: account.name,
      address: account.address,
      tag: 'Imported',
      showOwnName: true,
    })),
  ]

  const rawValue = getColumnFilterValue(
    columnFilters,
    COLUMN_IDS.SMART_ACCOUNT_ID,
  )
  const appliedIds = useMemo(() => {
    if (!rawValue) return [] as string[]
    const parsed = parseMultiSelectFilterValue(rawValue)
    return isError(parsed) ? ([] as string[]) : parsed
  }, [rawValue])

  const [localIds, setLocalIds] = useState<string[]>(appliedIds)
  const [search, setSearch] = useState('')
  // Sync pending selections when the applied value changes externally (URL
  // edit, or smart accounts finishing their async load on first render).
  const [syncedRawValue, setSyncedRawValue] = useState(rawValue)
  if (syncedRawValue !== rawValue) {
    setSyncedRawValue(rawValue)
    setLocalIds(appliedIds)
  }

  const query = search.trim().toLowerCase()
  const matchesQuery = (account: AccountOption) =>
    account.name.toLowerCase().includes(query) ||
    account.address.toLowerCase().includes(query)
  // A query flattens the list: collapsed matches surface beside the main
  // accounts instead of hiding behind the collapsible.
  const searchResults = query
    ? [...mainAccounts, ...collapsedAccounts].filter(matchesQuery)
    : []

  const toOption = (account: AccountOption) => ({
    value: account.id,
    label: account.name,
    display: () => (
      <div className="flex min-w-0 items-center gap-1.5">
        {account.showOwnName ? (
          <span className="flex h-5 min-w-0 items-center space-x-1.5 truncate text-sm leading-5">
            <SplitsAvatar address={account.address} size={16} />
            <span className="min-w-0 truncate text-primary">
              <HighlightedText text={account.name} highlight={search} />
            </span>
          </span>
        ) : (
          <AddressDisplay
            address={account.address}
            layout="standard"
            size="small"
            isInternal
            hidePopover
            highlightText={search}
          />
        )}
        {account.tag && (
          <span className="flex-shrink-0 text-xs text-secondary">
            {account.tag}
          </span>
        )}
      </div>
    ),
  })

  const isLocalEmpty = localIds.length === 0
  const isAppliedEmpty = appliedIds.length === 0
  const hasChanged =
    localIds.length !== appliedIds.length ||
    localIds.some((id) => !appliedIds.includes(id))

  const clearFilter = () => {
    setColumnFilters((prev) =>
      setColumnFilterValue(prev, COLUMN_IDS.SMART_ACCOUNT_ID, ''),
    )
  }

  const activeLabel = useMemo(() => {
    if (appliedIds.length === 0) return ''
    if (appliedIds.length === 1) {
      const [id] = appliedIds
      return (
        (
          smartAccounts.find((account) => account.id === id) ??
          importedAccounts.find((account) => account.id === id)
        )?.name ?? 'Account'
      )
    }
    return `Accounts (${appliedIds.length})`
  }, [appliedIds, smartAccounts, importedAccounts])

  return (
    <FilterPopoverButton
      label="Account"
      activeLabel={activeLabel}
      isAppliedEmpty={isAppliedEmpty}
      isLocalEmpty={isLocalEmpty}
      hasChanged={hasChanged}
      onApply={() => {
        setColumnFilters((prev) =>
          setColumnFilterValue(
            prev,
            COLUMN_IDS.SMART_ACCOUNT_ID,
            localIds.length > 0 ? JSON.stringify(localIds) : '',
          ),
        )
        // Apply/Reset close without going through onClose — clear here too.
        setSearch('')
      }}
      onReset={() => {
        setLocalIds([])
        setSearch('')
        clearFilter()
      }}
      onClear={() => {
        setLocalIds([])
        setSearch('')
        clearFilter()
      }}
      onClose={() => {
        setLocalIds(appliedIds)
        setSearch('')
      }}
    >
      <div className="flex flex-col gap-3">
        <Input
          inputSize="small"
          placeholder="Search by account or address..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          prefix={<SearchIcon className="h-3.5 w-3.5 text-secondary" />}
          onClearInput={search ? () => setSearch('') : undefined}
        />
        {query && searchResults.length === 0 ? (
          // Keep the label in place so the popover doesn't jump.
          <Group label="Account">
            <EmptyState className="py-4 text-center text-sm">
              No accounts found
            </EmptyState>
          </Group>
        ) : (
          <FilterScrollFade>
            <CheckboxGroupInput
              // Remount when the query clears so the collapsed group re-derives
              // its auto-expansion from selections made while searching.
              key={query ? 'search' : 'grouped'}
              label="Account"
              size="small"
              value={localIds}
              onChange={setLocalIds}
              options={
                query ? searchResults.map(toOption) : mainAccounts.map(toOption)
              }
              collapsedOptions={
                !query && collapsedAccounts.length > 0
                  ? {
                      options: collapsedAccounts.map(toOption),
                      showLabel: 'Show more',
                      hideLabel: 'Hide more',
                    }
                  : undefined
              }
            />
          </FilterScrollFade>
        )}
      </div>
    </FilterPopoverButton>
  )
}
